import {
  APIComponentAttributes,
  ArdoqId,
  LinkedComponent,
} from '@ardoq/api-types';
import {
  AggregatedReferenceType,
  ChildrenByParent,
  ComponentsById,
  GlobalReferenceTypeId,
  GraphModelShape,
} from '@ardoq/data-model';
import { ReferenceFilterContext } from '@ardoq/filter-helpers';
import { GraphInterface } from '../graphInterface/graphInterface';
import { createReferenceIdFromParentAndChildId } from '../utils/grouping/parentChildUtils';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import { range } from 'lodash';
import { getCurrentLocale, localeCompare } from '@ardoq/locale';

export const getWithoutFilter =
  (traversedReferenceIds: Set<ArdoqId>) =>
  ({ referenceId }: LinkedComponent) => {
    traversedReferenceIds.add(referenceId);
    return true;
  };

export const isSelectedReferenceType = (
  graphInterface: GraphInterface,
  referenceId: ArdoqId,
  referenceTypeIds: Set<GlobalReferenceTypeId>,
  parentReferenceIds: Set<string>
) => {
  if (referenceTypeIds.has(CommonDropdownOptions.ALL)) {
    return true;
  }

  const isParentReferenceType = parentReferenceIds.has(referenceId);
  if (isParentReferenceType) {
    return referenceTypeIds.has(CommonDropdownOptions.PARENT_AS_REFERENCE);
  }

  if (referenceTypeIds.has(CommonDropdownOptions.ALL_EXCEPT_PARENT_CHILD)) {
    return true;
  }
  const globalTypeId = graphInterface.getGlobalReferenceTypeId(referenceId);
  return globalTypeId ? referenceTypeIds.has(globalTypeId) : true;
};

export const getFilterByReferenceTypeAndGlobalFilters =
  (
    graphInterface: GraphInterface,
    referenceTypeIds: Set<GlobalReferenceTypeId> | null,
    parentReferenceIds: Set<string>,
    traversedReferenceIds: Set<ArdoqId>,
    ctx: ReferenceFilterContext
  ) =>
  ({ referenceId, componentId }: LinkedComponent) => {
    const reference = graphInterface.getReference(referenceId);
    const component = graphInterface.get(componentId);
    const isIncluded =
      (!referenceTypeIds ||
        isSelectedReferenceType(
          graphInterface,
          referenceId,
          referenceTypeIds,
          parentReferenceIds
        )) &&
      (!reference ||
        graphInterface.isReferenceIncludedInContextByFilter(reference, ctx)) &&
      (!component ||
        graphInterface.isComponentIncludedInContextByFilter(component, ctx));
    if (isIncluded) {
      traversedReferenceIds.add(referenceId);
    }
    return isIncluded;
  };

type SourceMap = GraphModelShape['sourceMap'];
type TargetMap = GraphModelShape['targetMap'];

/**
 * Immutable lookups consulted by ancestor/descendant traversal.
 * `excludedComponentIds` is applied at descent time (e.g. to hide
 * scenario-related components in scope-diff mode).
 */
type TraversalIndexes = {
  childrenByParent: ChildrenByParent;
  componentsById: ComponentsById;
  excludedComponentIds: Set<ArdoqId>;
};

/**
 * Mutable accumulators threaded through one traversal run.
 */
type TraversalState = {
  sourceMap: SourceMap;
  targetMap: TargetMap;
  addedReferences: Set<string>;
  visitedAncestors: Set<string>;
  visitedDescendants: Set<string>;
};

const addReference = (
  componentId: ArdoqId,
  childId: ArdoqId,
  state: TraversalState
) => {
  const referenceId = createReferenceIdFromParentAndChildId(
    componentId,
    childId
  );
  const isNew = !state.addedReferences.has(referenceId);
  if (isNew) {
    state.addedReferences.add(referenceId);

    if (!state.sourceMap.has(componentId)) {
      state.sourceMap.set(componentId, []);
    }
    state.sourceMap.get(componentId)!.push({
      referenceId,
      componentId: childId,
    });

    if (!state.targetMap.has(childId)) {
      state.targetMap.set(childId, []);
    }
    state.targetMap.get(childId)!.push({
      referenceId,
      componentId,
    });
  }
  return isNew;
};

const addDescendantReferences = (
  component: APIComponentAttributes,
  indexes: TraversalIndexes,
  state: TraversalState
) => {
  if (state.visitedDescendants.has(component._id)) {
    return;
  }
  state.visitedDescendants.add(component._id);
  const children = (indexes.childrenByParent.get(component._id) ?? []).filter(
    child => !indexes.excludedComponentIds.has(child._id)
  );
  children.forEach(child => {
    const isNew = addReference(component._id, child._id, state);
    if (isNew) {
      addDescendantReferences(child, indexes, state);
    }
  });
};

const addAncestorReferences = (
  component: APIComponentAttributes,
  indexes: TraversalIndexes,
  state: TraversalState
) => {
  if (state.visitedAncestors.has(component._id)) {
    return;
  }
  state.visitedAncestors.add(component._id);
  const parentId = component.parent;
  if (!parentId) return;
  const parent = indexes.componentsById[parentId];
  if (!parent) return;
  addReference(parent._id, component._id, state);
  addAncestorReferences(parent, indexes, state);
  addDescendantReferences(parent, indexes, state);
};

const copyListMap = (map: Map<string, any[]>) =>
  new Map([...map].map(([key, list]) => [key, list.slice()]));

export const addAncestorAndDescendantReferencesOfComponent = (
  component: APIComponentAttributes,
  indexes: TraversalIndexes,
  state: TraversalState
) => {
  addAncestorReferences(component, indexes, state);
  addDescendantReferences(component, indexes, state);
  return { sourceMap: state.sourceMap, targetMap: state.targetMap };
};

export const addAncestorAndDescendantReferences = (
  indexes: TraversalIndexes,
  sourceMap: SourceMap,
  targetMap: TargetMap,
  startSet: ArdoqId[]
) => {
  const state: TraversalState = {
    sourceMap: copyListMap(sourceMap),
    targetMap: copyListMap(targetMap),
    addedReferences: new Set<string>(),
    visitedAncestors: new Set<string>(),
    visitedDescendants: new Set<string>(),
  };
  const addForComponentId = (componentId: ArdoqId) => {
    const component = indexes.componentsById[componentId];
    if (!component) return;
    addAncestorAndDescendantReferencesOfComponent(component, indexes, state);
  };
  Array.from(sourceMap.keys()).forEach(addForComponentId);
  Array.from(targetMap.keys()).forEach(addForComponentId);
  Array.from(startSet).forEach(addForComponentId);
  return {
    sourceMap: state.sourceMap,
    targetMap: state.targetMap,
    parentReferenceIds: state.addedReferences,
  };
};

export const joinSets = <T>(...args: Iterable<T>[]): Set<T> =>
  new Set(args.flatMap(arg => Array.from(arg)));

const extendSet = <T>(set: Set<T>, more: T[]) =>
  more.forEach(item => set.add(item));

const subtractSets = <T>(
  iterableA: Iterable<T>,
  iterableB: Iterable<T>
): Set<T> => {
  const listA = Array.from(iterableA);
  const setB = new Set(iterableB);
  return new Set(listA.filter(item => !setB.has(item)));
};

export const traverse = (
  startSet: ArdoqId[],
  map: Map<ArdoqId, LinkedComponent[]>,
  depth: number,
  filter: (linkedComponent: LinkedComponent) => boolean
) => {
  const nodes = new Set<ArdoqId>();
  const visitedNodes = new Set<ArdoqId>();

  range(depth).reduce(startSet => {
    extendSet(visitedNodes, startSet);
    const childrenIds = startSet
      .flatMap(id => map.get(id) || [])
      .filter(filter)
      .map(({ componentId }) => componentId);

    extendSet(nodes, childrenIds);
    return childrenIds.length === 0
      ? childrenIds
      : Array.from(subtractSets(childrenIds, visitedNodes));
  }, startSet);
  return nodes;
};

export const mapToDictionaryList = (
  graphInterface: GraphInterface,
  set: Set<ArdoqId>
): AggregatedReferenceType[] => {
  const locale = getCurrentLocale();

  return Array.from(
    Array.from(set)
      .reduce((map, referenceId: ArdoqId) => {
        const referenceType =
          graphInterface.getGlobalReferenceType(referenceId);
        if (referenceType) {
          if (!map.has(referenceType.name)) {
            map.set(referenceType.name, new Set<GlobalReferenceTypeId>());
          }
          map.get(referenceType.name)!.add(referenceType.id);
        }
        return map;
      }, new Map<string, Set<GlobalReferenceTypeId>>())
      .entries()
  )
    .map(([name, typeIds]) => ({ name, typeIds: Array.from(typeIds) }))
    .sort((a, b) => localeCompare(a.name, b.name, locale));
};

export const getAllReferences = (
  graphInterface: GraphInterface,
  referenceMap: GraphModelShape['referenceMap'],
  componentIds: ArdoqId[] | Set<ArdoqId>,
  ctx: ReferenceFilterContext
) => {
  const componentIdSet = new Set(componentIds);
  return Array.from(referenceMap)
    .filter(([referenceId, { sourceId, targetId }]) => {
      if (!componentIdSet.has(sourceId) || !componentIdSet.has(targetId)) {
        return false;
      }
      const reference = graphInterface.getReference(referenceId);
      if (!reference) return false;
      return graphInterface.isReferenceIncludedInContextByFilter(
        reference,
        ctx
      );
    })
    .map(([referenceId]) => referenceId);
};
